import { reactive, computed } from "vue";
import {
  getUserItem,
  updateUser,
  createUser,
  removeUser,
} from "@/entities/user";
import { getOrganizationList } from "@/entities/organization";
import { getRoleList } from "@/entities/role";
import { getMe } from "@/entities/session";
import {
  USER_PUT_FIELDS,
  USER_POST_FIELDS,
  clone,
  pickFields,
  normalizeRoles,
  emptyUserData,
} from "@/widgets/user-profile-drawer/model/userFields";

/**
 * Composable for the user drawer.
 *
 * uuid-driven, no "own/self" branch. To view your own profile, the
 * caller passes the logged-in user's uuid like any other user.
 *
 * Modes:
 *   - "create" — POST /users/
 *   - "edit"   — GET /users/{uuid} → PUT /users/{uuid}
 *   - "view"   — GET /users/{uuid} (read-only)
 */
export function useUserDrawerData() {
  const state = reactive({
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    data: {},
    original: {},
    mode: "view",
    userUuid: null,
    editMode: false,
    self: false,
    organizations: [],
    roles: [],
  });

  const dirty = computed(
    () => JSON.stringify(state.data) !== JSON.stringify(state.original)
  );

  let dictionariesLoaded = false;

  async function ensureDictionaries(self = false) {
    if (dictionariesLoaded) return;
    try {
      const orgsResp = await getOrganizationList({});
      if (orgsResp?.success) state.organizations = orgsResp.payload || [];
      if (!self) {
        const rolesResp = await getRoleList({});
        if (rolesResp?.success) state.roles = rolesResp.payload?.items || [];
      }
      dictionariesLoaded = true;
    } catch (e) {
      console.error(e);
    }
  }

  async function load(uuid, mode = "view", opts = {}) {
    state.loading = true;
    state.error = null;
    state.mode = mode;
    state.editMode = mode === "edit" || mode === "create";
    state.userUuid = uuid || null;
    state.self = !!opts.self;
    state.data = {};
    state.original = {};

    await ensureDictionaries(opts.self);

    if (mode === "create") {
      const seed = emptyUserData();
      state.data = clone(seed);
      state.original = clone(seed);
      state.loading = false;
      return;
    }

    if (!uuid && !opts.self) {
      state.error = new Error("No user uuid");
      state.loading = false;
      return;
    }

    try {
      const res = opts.self ? await getMe() : await getUserItem(uuid);
      const payload = res?.payload ?? res ?? {};
      const normalized = normalizeRoles(payload);
      const merged = { ...emptyUserData(), ...normalized };
      state.data = clone(merged);
      state.original = clone(merged);
    } catch (e) {
      console.error(e);
      state.error = e;
    } finally {
      state.loading = false;
    }
  }

  function update(key, value) {
    state.data[key] = value;
  }

  function cancel() {
    state.data = clone(state.original);
    state.editMode = state.mode === "create";
  }

  function startEdit() {
    state.editMode = true;
    if (state.mode === "view") state.mode = "edit";
  }

  async function save() {
    if (state.saving) return null;
    state.saving = true;
    try {
      if (state.mode === "create") {
        const payload = pickFields(state.data, USER_POST_FIELDS);
        const resp = await createUser(payload);
        return resp;
      }
      if (state.mode === "edit" && state.userUuid) {
        const payload = pickFields(state.data, USER_PUT_FIELDS);
        const resp = await updateUser(state.userUuid, payload);
        state.original = clone(state.data);
        state.editMode = false;
        state.mode = "view";
        return resp;
      }
      return null;
    } finally {
      state.saving = false;
    }
  }

  async function remove() {
    if (!state.userUuid || state.deleting) return null;
    state.deleting = true;
    try {
      return await removeUser(state.userUuid);
    } finally {
      state.deleting = false;
    }
  }

  return {
    state,
    dirty,
    load,
    update,
    cancel,
    startEdit,
    save,
    remove,
  };
}
